import gql from 'graphql-tag';

export const GET_AMAZON_API_METRICS = gql`
  query InternalAmazonApiMetrics(
    $startDate: String!
    $endDate: String!
    $operations: [String!]
    $systems: [String!]
    $bucketSizeMinutes: Int
  ) {
    internalAmazonApiMetrics(
      startDate: $startDate
      endDate: $endDate
      operations: $operations
      systems: $systems
      bucketSizeMinutes: $bucketSizeMinutes
    ) {
      buckets {
        timestamp
        totalRequests
        successCount
        failureCount
        avgDurationMs
      }
      byOperation {
        operation
        count
        successRate
        avgDurationMs
      }
      liveStatus {
        feedBudgetUsed
        feedBudgetLimit
        queueDepth
        oldestQueueAgeMs
        pendingCount
        claimedCount
        submittingCount
        failedCount
        activeHubSessions
        invalidHubSessions
        expiredHubSessions
      }
      recentRequests {
        id
        system
        operation
        hubCounterId
        isStaging
        statusCode
        success
        durationMs
        errorCode
        errorMessage
        createdAt
      }
      recentErrors {
        operation
        errorCode
        errorMessage
        statusCode
        count
        lastOccurred
      }
      feeds {
        id
        hubCounterId
        userId
        dropPointId
        feedId
        operation
        status
        noProcessed
        noSuccess
        noFailed
        submittedAt
        completedAt
      }
      queue {
        id
        hubCounterId
        operation
        status
        reasons
        attempts
        feedId
        nextAttemptAt
      }
      totalRequests
      totalErrors
      p95DurationMs
      p99DurationMs
    }
  }
`;

export const GET_AMAZON_FEED_DETAILS = gql`
  query GET_AMAZON_MONITOR_FEED_DETAILS($feedId: String!) {
    amazonFeed(feedId: $feedId) {
      documents
    }
  }
`;

export interface MetricBucket {
  timestamp: string;
  totalRequests: number;
  successCount: number;
  failureCount: number;
  avgDurationMs: number;
}

export interface OperationBreakdown {
  operation: string;
  count: number;
  successRate: number;
  avgDurationMs: number;
}

export interface LiveStatus {
  feedBudgetUsed: number;
  feedBudgetLimit: number;
  queueDepth: number;
  oldestQueueAgeMs: number | null;
  pendingCount: number;
  claimedCount: number;
  submittingCount: number;
  failedCount: number;
  activeHubSessions: number;
  invalidHubSessions: number;
  expiredHubSessions: number;
}

export interface RecentRequest {
  id: string;
  system: string;
  operation: string;
  hubCounterId: string | null;
  isStaging: boolean;
  statusCode: number | null;
  success: boolean;
  durationMs: number;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ErrorGroup {
  operation: string;
  errorCode: string | null;
  errorMessage: string | null;
  statusCode: number | null;
  count: number;
  lastOccurred: string;
}

export interface MonitorFeed {
  id: string;
  hubCounterId: string | null;
  userId: string | null;
  dropPointId: string | null;
  feedId: string | null;
  operation: string;
  status: string;
  noProcessed: number | null;
  noSuccess: number | null;
  noFailed: number | null;
  submittedAt: string;
  completedAt: string | null;
}

export interface MonitorQueueItem {
  id: string;
  hubCounterId: string;
  operation: string;
  status: string;
  reasons: string[];
  attempts: number;
  feedId: string | null;
  nextAttemptAt: string | null;
}

export interface AmazonApiMetricsResponse {
  buckets: MetricBucket[];
  byOperation: OperationBreakdown[];
  liveStatus: LiveStatus;
  recentRequests: RecentRequest[];
  recentErrors: ErrorGroup[];
  feeds: MonitorFeed[];
  queue: MonitorQueueItem[];
  totalRequests: number;
  totalErrors: number;
  p95DurationMs: number;
  p99DurationMs: number;
}

export interface GetAmazonApiMetricsData {
  internalAmazonApiMetrics: AmazonApiMetricsResponse;
}

export interface GetAmazonApiMetricsVars {
  startDate: string;
  endDate: string;
  operations?: string[];
  systems?: string[];
  bucketSizeMinutes?: number;
}

export const OPERATION_LABELS: Record<string, string> = {
  packages: 'Inventar',
  packagesCulled: 'packagesCulled',
  driverDelivery: 'driverDelivery',
  driverPickup: 'driverPickup',
  packageEvents: 'Paket-Event',
  taskEvents: 'taskEvents',
  tasks: 'tasks',
  storeHours: 'storeHours',
  configuration: 'configuration',
  authorize: 'authorize',
  'session.verify': 'session.verify',
  'feeds.submit': 'Feed senden',
  'feeds.list': 'feeds.list',
  'feeds.get': 'feeds.get',
  'feeds.document': 'feeds.document',
  'lwa.token': 'lwa.token',
};

export const amazonOperationLabel = (operation: string): string => OPERATION_LABELS[operation] || operation;

export const ENDPOINT_GROUPS: Record<string, { label: string; operations: string[] }> = {
  hub: {
    label: 'Hub',
    operations: [
      'packages',
      'packagesCulled',
      'driverDelivery',
      'driverPickup',
      'packageEvents',
      'taskEvents',
      'tasks',
      'storeHours',
      'configuration',
      'authorize',
      'session.verify',
    ],
  },
  feeds: {
    label: 'Feeds',
    operations: ['feeds.submit', 'feeds.list', 'feeds.get', 'feeds.document', 'lwa.token'],
  },
};

export const SYSTEM_OPTIONS = [
  { value: 'HUB', label: 'Hub' },
  { value: 'FEEDS', label: 'Feeds' },
];

export const formatOldestQueueAge = (ms: number | null | undefined): string => {
  if (!ms) return 'No pending jobs';
  if (ms >= 60_000) return `Oldest: ${Math.round(ms / 60_000)}m`;
  return `Oldest: ${Math.round(ms / 1000)}s`;
};

export const collectFeedDocumentErrors = (documents?: any[] | null): any[] =>
  (documents || []).flatMap((document) => document?.errors || []).filter(Boolean);
